import {InfoIcon} from '@chakra-ui/icons';
import {HStack, Text, VStack} from '@chakra-ui/layout';
import {Link} from '@chakra-ui/react';
import {ExceptionsMatch, RapportMatch} from 'api-types/ffconnect';
import {dateAvecSlashes, routes} from 'ff-common';
import {useTranslation} from 'react-i18next';
import {buildRoute} from '@synako/enhanced-router';

/**
 * Composant permettant d'afficher les alertes ou les bloquants liés à une commande PDF
 * en haut du panier.
 */
export const PanierPdfAlertes: React.FC<{
  exceptions?: RapportMatch['exceptions'];
}> = ({exceptions}) => {
  if (!exceptions) return null;

  const {alertes, bloquants} = exceptions;
  if (alertes.length === 0 && bloquants.length === 0) return null;

  return (
    <VStack
      w='100%'
      alignItems='stretch'
      spacing='2'
      my='2'
    >
      {/* Affichage des messages bloquants */}
      {bloquants.map((bloquant, index) => (
        <BandeauException
          key={index}
          exception={bloquant}
          bg='red.warning_bg'
          color='red.warning'
        />
      ))}
      {/* Affichage des messages d'alerte */}
      {alertes.map((alerte, index) => (
        <BandeauException
          key={index}
          exception={alerte}
          bg='yellow.warning'
          color='yellow.warning_text'
        />
      ))}
    </VStack>
  );
};

type AlerteMatch = ExceptionsMatch['alertes'][number];
type BloquantMatch = ExceptionsMatch['bloquants'][number];

const BandeauException: React.FC<{
  exception: AlerteMatch | BloquantMatch;
  bg: string;
  color: string;
}> = ({exception, bg, color}) => {
  const {t, i18n} = useTranslation();
  // Les deux familles ne partagent que `code` et `message` : les champs du
  // fichier doublon n'existent que sur les alertes.
  const {
    date_doublon,
    utilisateur_initiateur,
    processus_id,
    societe_commerciale_id,
  }: Partial<AlerteMatch> = exception;

  // `fichier_doublon` est la seule exception dont le libellé attend des
  // paramètres, et il affiche la date à la fois en jour et en heure. Seule
  // l'heure impose un objet Date (`dateAvecSlashes` prend la chaîne ISO telle
  // quelle), d'où cette conversion isolée. Les autres codes ignorent ces
  // valeurs : les passer systématiquement évite un cas particulier.
  const dateDoublon = date_doublon ? new Date(date_doublon) : undefined;

  // Le `message` du serveur n'est pas traduit : il ne sert que de repli si un
  // nouveau code d'exception apparaît avant sa clé de traduction.
  const libelle =
    t(`back_office.pdf_order_import.match.exception.${exception.code}`, {
      defaultValue: '',
      date_doublon: date_doublon ? dateAvecSlashes(date_doublon) : '',
      heure_doublon:
        dateDoublon?.toLocaleTimeString(i18n.language, {
          hour: '2-digit',
          minute: '2-digit',
        }) ?? '',
      utilisateur_initiateur: utilisateur_initiateur ?? '',
    }) || exception.message;

  // Seul le fichier doublon mène vers l'écran de revue du processus PDF qui
  // avait déjà traité le fichier. `ExceptionsMatch` déclare ces deux
  // identifiants optionnels, car les autres alertes n'en portent pas:
  // il faut donc les écarter avant de construire la route.
  const lienVersProcessus =
    processus_id && societe_commerciale_id
      ? buildRoute(routes.client.backOfficeParsePdfOrderProcessus, {
          id_societe_commerciale: societe_commerciale_id,
          processus_pdf_id: processus_id,
        })
      : undefined;

  return (
    <HStack
      bg={bg}
      color={color}
      borderRadius='8px'
      px='3'
      py='2'
      spacing='2'
    >
      <InfoIcon />
      <Text fontSize='xs'>
        {libelle}
        {lienVersProcessus && (
          // lien qui recharge la page : mode commercial => BO
          <Link
            href={lienVersProcessus}
            color='blue.main'
            textDecoration='underline'
            ml={1}
          >
            {t(
              'back_office.pdf_order_import.match.exception.fichier_doublon_lien',
            )}
          </Link>
        )}
      </Text>
    </HStack>
  );
};
